// Human-readable one-liner for a credited-reward / coupon
// `redemption_conditions` snapshot. Mirrors the Python helper
// `core.services.coupon_conditions.format_conditions_short`, kept in sync
// so the same wording shows up on both the customer dashboard and the
// staff app card. Returns "" when the reward has no constraints, so the
// caller can show a clean card.
//
// Example:  { allowed_weekdays: [0,1,2,3], time_from: '12:00' }
//        => "Lun · Mar · Mer · Gio · 12:00-..."
const DAY_NAMES_SHORT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export function formatRedemptionConditionsShort(conditions) {
  if (!conditions || typeof conditions !== 'object') return '';
  const parts = [];
  const days = Array.isArray(conditions.allowed_weekdays) ? conditions.allowed_weekdays : [];
  if (days.length) {
    const sorted = [...days].sort((a, b) => a - b);
    const names = sorted.filter((d) => d >= 0 && d <= 6).map((d) => DAY_NAMES_SHORT[d]);
    parts.push(names.join(' · '));
  }
  const { time_from: tf, time_until: tu } = conditions;
  if (tf || tu) parts.push(`${tf || '--:--'}-${tu || '--:--'}`);
  if (conditions.min_visits) parts.push(`min ${conditions.min_visits} visite`);
  const minSpend = conditions.min_spend_visit || conditions.min_spend;
  if (minSpend) parts.push(`min ${minSpend}€`);
  if (conditions.min_wait_days) parts.push(`dopo ${conditions.min_wait_days}g`);
  return parts.join(' · ');
}

/** Returns true if the reward CAN be redeemed RIGHT NOW (Italian local time). */
export function isRedeemableNow(conditions, customer = {}) {
  if (!conditions || typeof conditions !== 'object') return true;
  // Compute Italian local time using Intl (handles DST automatically).
  const fmt = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  // Weekday: cheap mapping from the formatted Italian abbreviation.
  const partsArr = fmt.formatToParts(new Date());
  const wdLabel = partsArr.find((p) => p.type === 'weekday')?.value || '';
  const wdIndex = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom']
    .findIndex((d) => wdLabel.toLowerCase().startsWith(d));
  const hour = Number(partsArr.find((p) => p.type === 'hour')?.value || '0');
  const minute = Number(partsArr.find((p) => p.type === 'minute')?.value || '0');

  const days = Array.isArray(conditions.allowed_weekdays) ? conditions.allowed_weekdays : [];
  if (days.length && wdIndex >= 0 && !days.includes(wdIndex)) return false;

  const mins = hour * 60 + minute;
  if (conditions.time_from) {
    const [h, m] = String(conditions.time_from).split(':').map(Number);
    if (mins < (h || 0) * 60 + (m || 0)) return false;
  }
  if (conditions.time_until) {
    const [h, m] = String(conditions.time_until).split(':').map(Number);
    if (mins > (h || 0) * 60 + (m || 0)) return false;
  }
  if (conditions.min_visits && Number(customer.total_visits || 0) < conditions.min_visits) return false;
  return true;
}
